// Utilerías del popup de Embedded Signup de Meta para la vinculación de líneas, incluida la coexistencia.
// Portado de Chatwoot upstream. FB.login() devuelve un `code`; los identificadores del
// negocio (waba_id, phone_number_id) llegan por separado vía postMessage desde el popup.

// Carga del SDK compartida con los demás canales de Meta.
import {
  setupFacebookSdk,
  facebookSdkLocale,
} from 'dashboard/helper/facebookSdk';

export { facebookSdkLocale };

// Configuración pública del entorno (app de Meta, versión de Graph, Configuration ID).
const chatwootConfig = () => window.chatwootConfig || {};

// Deja el SDK listo antes del clic: los navegadores solo abren popups dentro de la ventana de
// activación del usuario y una descarga lenta del SDK en ese momento la agota. Un fallo aquí no
// se propaga: runEmbeddedSignup vuelve a intentar la carga y ahí sí se informa.
export const preloadFacebookSdk = async () => {
  if (!chatwootConfig().fbAppId) return;
  try {
    await setupFacebookSdk();
  } catch (e) {
    // Sin red o bloqueado por el navegador; se reintenta al pulsar el botón.
  }
};

// Host del popup de Meta y tipo de los mensajes que manda por postMessage.
const FACEBOOK_HOST = 'facebook.com';
const SIGNUP_MESSAGE_TYPE = 'WA_EMBEDDED_SIGNUP';
// Motivo con el que FB.login rechaza cuando el usuario cierra el popup sin iniciar sesión.
const LOGIN_CANCELLED = 'Login cancelled';
// Motivo de rechazo cuando Meta cierra el flujo sin un número que conectar.
export const UNSUPPORTED_FLOW = 'UNSUPPORTED_FLOW';
// Valor de provider_config.source que distingue las bandejas creadas con el popup de Meta.
// Debe coincidir con Channel::Whatsapp::EMBEDDED_SIGNUP_SOURCE en el backend (sincronía manual).
export const EMBEDDED_SIGNUP_SOURCE = 'embedded_signup';

// Bandeja WhatsApp Cloud dada de alta o reconectada con el popup de Meta (app de Intersel).
export const isEmbeddedSignupInbox = inbox => {
  const config = (inbox && inbox.provider_config) || {};
  return config.source === EMBEDDED_SIGNUP_SOURCE;
};

// Si el número de la bandeja vive también en la app de WhatsApp Business, según el dato que guarda
// el backend (provider_config.on_biz_app); null cuando la bandeja aún no lo tiene.
export const storedOnBizApp = inbox => {
  const config = (inbox && inbox.provider_config) || {};
  return typeof config.on_biz_app === 'boolean' ? config.on_biz_app : null;
};

// El camino asistido existe solo si el entorno tiene la app de Meta y el Configuration ID
// de Embedded Signup; sin ellos el wizard ofrece únicamente el alta manual.
export const isEmbeddedSignupAvailable = () => {
  const config = chatwootConfig();
  return Boolean(config.fbAppId && config.whatsappConfigurationId);
};

export const isValidBusinessData = businessData =>
  Boolean(businessData && businessData.business_id && businessData.waba_id);

// Eventos de cierre del popup que Conversa sí sabe procesar.
const FINISH_EVENTS = ['FINISH', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'];
// Evento con el que el popup reporta un fallo; el detalle viene en data.error_message.
const ERROR_EVENT = 'ERROR';
const CANCEL_EVENT = 'CANCEL';
// Nombre de los errores cuyo mensaje escribió Meta en el popup y sí puede mostrarse al usuario.
export const POPUP_ERROR_NAME = 'EmbeddedSignupPopupError';
// Cierres válidos para Meta pero sin número que conectar (solo WABA, migración OBO, solo API).
const UNSUPPORTED_FINISH_EVENTS = [
  'FINISH_ONLY_WABA',
  'FINISH_OBO_MIGRATION',
  'FINISH_GRANT_ONLY_API_ACCESS',
];
// Tiempo de espera del evento FINISH después de recibir el code; pasado esto se da por cancelado.
// FB.login entrega el code al cerrarse el popup y el FINISH llega por postMessage casi al mismo
// tiempo (en pruebas, menos de 2 s). Si el usuario cerró el popup después de iniciar sesión el
// FINISH nunca llega; 20 s cubre redes lentas sin dejar el botón "conectando" indefinidamente.
export const FINISH_TIMEOUT_MS = 20000;
// Motivo de rechazo cuando quien lanzó el flujo lo cancela (por ejemplo, al salir de la pantalla).
export const SIGNUP_ABORTED = 'SIGNUP_ABORTED';

// Solo se aceptan mensajes del popup de Meta: https y host facebook.com o un subdominio suyo
// (www., web., business.). Un sufijo suelto dejaría pasar dominios como "evilfacebook.com".
export const isFacebookOrigin = origin => {
  try {
    const { protocol, hostname } = new URL(origin);
    return (
      protocol === 'https:' &&
      (hostname === FACEBOOK_HOST || hostname.endsWith(`.${FACEBOOK_HOST}`))
    );
  } catch (e) {
    return false;
  }
};

// Mensaje de error que manda el popup (Meta lo anida en data; versiones viejas lo traían suelto).
const signupErrorMessage = data =>
  (data.data && data.data.error_message) || data.error_message || '';

// Error del popup. Solo se marca como mostrable si Meta mandó un texto; sin él la UI pone su
// propio mensaje genérico, ya traducido.
const popupError = message => {
  const error = new Error(message || 'Signup error');
  if (message) error.name = POPUP_ERROR_NAME;
  return error;
};

export const createMessageHandler = onEmbeddedSignupData => event => {
  if (!isFacebookOrigin(event.origin)) return;

  try {
    let data;
    if (typeof event.data === 'string') {
      data = JSON.parse(event.data);
    } else if (typeof event.data === 'object' && event.data !== null) {
      data = event.data;
    } else {
      return;
    }

    if (data.type === SIGNUP_MESSAGE_TYPE) {
      onEmbeddedSignupData(data);
    }
  } catch (e) {
    // Mensajes no-JSON o irrelevantes del popup: se ignoran.
  }
};

// Extras del popup según el modo. Con coexistencia (featureType whatsapp_business_app_onboarding)
// Meta muestra el QR de vinculación de la app de WhatsApp Business; sin él abre el flujo normal,
// que deja elegir un WABA y un número que ya están en Cloud API.
export const signupExtras = ({ coexistence = true } = {}) => {
  const extras = { setup: {}, sessionInfoVersion: '3' };
  if (coexistence) extras.featureType = 'whatsapp_business_app_onboarding';
  return extras;
};

// Abre el popup de Meta y resuelve con el code de autorización.
export const initWhatsAppEmbeddedSignup = (configId, options) =>
  new Promise((resolve, reject) => {
    window.FB.login(
      response => {
        if (response.authResponse && response.authResponse.code) {
          resolve(response.authResponse.code);
        } else if (response.error) {
          reject(new Error(response.error));
        } else {
          reject(new Error(LOGIN_CANCELLED));
        }
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: signupExtras(options),
      }
    );
  });

// Corre el flujo completo: SDK -> FB.login (code) + postMessage (waba/phone) y resuelve
// cuando tiene ambos. Resuelve null si el usuario cancela el popup en cualquier punto:
// antes de iniciar sesión (FB.login sin code), a mitad de los pasos (evento CANCEL) o
// cerrándolo después del login sin que llegue FINISH (temporizador de seguridad).
// Rechaza con error UNSUPPORTED_FLOW si Meta cierra el flujo sin un número que conectar.
// La promesa devuelta expone cancel(): quita el listener de window y rechaza con SIGNUP_ABORTED,
// para que un componente que se destruye con el popup abierto no procese eventos tardíos.
// options.coexistence (por defecto true) elige el modo del popup; ver signupExtras.
export const runEmbeddedSignup = options => {
  let settle;
  let abort;

  const promise = new Promise((resolve, reject) => {
    let authCode = null;
    let businessData = null;
    let settled = false;
    let messageHandler;
    let finishTimer = null;

    settle = (fn, value) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', messageHandler);
      if (finishTimer) clearTimeout(finishTimer);
      fn(value);
    };
    abort = () => settle(reject, new Error(SIGNUP_ABORTED));

    const resolveIfReady = () => {
      if (!authCode || !businessData) return;
      settle(resolve, {
        code: authCode,
        business_id: businessData.business_id,
        waba_id: businessData.waba_id,
        phone_number_id: businessData.phone_number_id || '',
      });
    };

    messageHandler = createMessageHandler(data => {
      if (FINISH_EVENTS.includes(data.event)) {
        if (!isValidBusinessData(data.data)) {
          settle(reject, new Error('Invalid business data'));
          return;
        }
        businessData = data.data;
        resolveIfReady();
      } else if (UNSUPPORTED_FINISH_EVENTS.includes(data.event)) {
        settle(reject, new Error(UNSUPPORTED_FLOW));
        // Meta documenta el evento en mayúsculas; versiones anteriores del popup mandaban "error".
      } else if (String(data.event).toUpperCase() === ERROR_EVENT) {
        settle(reject, popupError(signupErrorMessage(data)));
      } else if (data.event === CANCEL_EVENT) {
        // Un CANCEL con error_message es un fallo que Meta mostró al usuario, no un cierre voluntario.
        const message = signupErrorMessage(data);
        if (message) settle(reject, popupError(message));
        else settle(resolve, null);
      }
    });

    window.addEventListener('message', messageHandler);

    (async () => {
      try {
        await setupFacebookSdk();
        // Cancelado mientras cargaba el SDK (se salió de la pantalla): no abrir el popup.
        if (settled) return;
        authCode = await initWhatsAppEmbeddedSignup(
          chatwootConfig().whatsappConfigurationId,
          options
        );
        resolveIfReady();
        // Con code pero sin FINISH el popup ya se cerró: no dejar el botón esperando.
        if (!settled) {
          finishTimer = setTimeout(
            () => settle(resolve, null),
            FINISH_TIMEOUT_MS
          );
        }
      } catch (error) {
        // FB.login() rechaza con LOGIN_CANCELLED cuando el usuario cierra el popup.
        if (error.message === LOGIN_CANCELLED) {
          settle(resolve, null);
        } else {
          settle(reject, error);
        }
      }
    })();
  });

  promise.cancel = abort;
  return promise;
};
